/**
 * Pure, React-free helpers for the compact task-estimate: id generation, the
 * WIZARD OPTION METADATA (the human-readable choices for each of the 4 steps),
 * blank-input factories, validation, and the enum coercion the AI suggestion
 * path relies on.
 *
 * The estimation MATH lives in {@link ../engine/estimation}; this file is the
 * presentation/plumbing glue that drives the 4-step wizard and maps its answers
 * onto the engine's inputs. The engine's own label maps are internal, so the
 * display metadata (labels + short descriptions + the common/advanced split for
 * extra work) lives here, referencing the engine's exported types.
 *
 * Pure TS — no React, no I/O.
 */

import { v4 as uuid } from 'uuid';
import {
  estimate,
  type ChangeType,
  type ComplexityFactor,
  type EstimateInputs,
  type EstimateResult,
  type ExtraWorkItem,
  type Unknowns,
} from '../engine/estimation';
import type { AiEstimateSuggestion, EstimateOverride } from './types';

/** Deterministic document id for a new task estimate. */
export function taskEstimateDocId(): string {
  return `taskest_${uuid()}`;
}

// ---------------------------------------------------------------------------
// Step 1 — Change type (radio, §5.1)
// ---------------------------------------------------------------------------

export interface ChangeTypeOption {
  value: ChangeType;
  label: string;
  /** One-line description shown under the label. */
  description: string;
}

/** The change-type choices, in ascending-effort order. */
export const CHANGE_TYPE_OPTIONS: ChangeTypeOption[] = [
  { value: 'config', label: 'Config change', description: 'A setting, flag, or config-only tweak.' },
  { value: 'bug', label: 'Bug fix', description: 'Fixing incorrect behavior in existing code.' },
  { value: 'feature', label: 'Feature', description: 'A new, self-contained capability.' },
  { value: 'multi_part', label: 'Multi-part change', description: 'Several coordinated changes in one area.' },
  {
    value: 'cross_service_migration',
    label: 'Cross-service migration',
    description: 'Change spanning multiple services or a migration.',
  },
  { value: 'spike', label: 'Spike', description: 'A time-boxed investigation, not a delivery.' },
  {
    value: 'custom',
    label: 'Other / custom…',
    description: 'A change type not listed — set your own label and base effort.',
  },
];

// ---------------------------------------------------------------------------
// Step 2 — What makes it harder (checkboxes, §5.2 — the 4 complexity factors)
// ---------------------------------------------------------------------------

export interface ComplexityOption {
  value: ComplexityFactor;
  label: string;
  description: string;
}

/** The four complexity factors that make a change riskier / larger. */
export const COMPLEXITY_OPTIONS: ComplexityOption[] = [
  {
    value: 'changes_prod_behavior',
    label: 'Changes production behavior',
    description: 'Alters what customers/systems experience in prod.',
  },
  {
    value: 'legacy_unfamiliar',
    label: 'Legacy / unfamiliar code',
    description: 'Touches code you don’t know well or that is old.',
  },
  {
    value: 'never_done_before',
    label: 'Never done before',
    description: 'No existing pattern to follow on the team.',
  },
  {
    value: 'multiple_services',
    label: 'Touches multiple services',
    description: 'Work crosses more than one service boundary.',
  },
];

// ---------------------------------------------------------------------------
// Step 3 — Extra work (common checkboxes + "+ More" for advanced, §5.3)
// ---------------------------------------------------------------------------

export interface ExtraWorkOption {
  value: ExtraWorkItem;
  label: string;
  description: string;
}

/** The commonly-selected extra-work items (shown up-front). */
export const EXTRA_WORK_COMMON: ExtraWorkOption[] = [
  { value: 'design', label: 'Design', description: 'A short design / approach doc or review.' },
  { value: 'integration_testing', label: 'Integration testing', description: 'Testing across component boundaries.' },
  { value: 'documentation', label: 'Documentation', description: 'Runbooks, READMEs, or API docs.' },
  { value: 'e2e_regression', label: 'E2E regression', description: 'End-to-end regression coverage.' },
];

/** The advanced extra-work items (revealed behind "+ More"). */
export const EXTRA_WORK_ADVANCED: ExtraWorkOption[] = [
  { value: 'load_testing', label: 'Load testing', description: 'Performance / load validation.' },
  { value: 'new_infra', label: 'New infrastructure', description: 'Provision new infra / resources.' },
  { value: 'mcm_approval', label: 'MCM approval', description: 'A change-management approval is needed.' },
  { value: 'cross_team', label: 'Cross-team coordination', description: 'Requires another team’s involvement.' },
];

/** All extra-work options (common first, then advanced). */
export const EXTRA_WORK_ALL: ExtraWorkOption[] = [
  ...EXTRA_WORK_COMMON,
  ...EXTRA_WORK_ADVANCED,
];

// ---------------------------------------------------------------------------
// Step 4 — Unknowns (radio; 'major' triggers the Spike recommendation, §5.4)
// ---------------------------------------------------------------------------

export interface UnknownsOption {
  value: Unknowns;
  label: string;
  description: string;
}

/** How well-understood the work is (drives confidence + spike timebox). */
export const UNKNOWNS_OPTIONS: UnknownsOption[] = [
  {
    value: 'well_understood',
    label: 'Well understood',
    description: 'The approach is clear; few open questions.',
  },
  {
    value: 'some',
    label: 'Some unknowns',
    description: 'A few things to figure out along the way.',
  },
  {
    value: 'major',
    label: 'Major unknowns',
    description: 'Significant open questions — hard to estimate.',
  },
];

// ---------------------------------------------------------------------------
// Enum guards + coercion (used by the AI suggestion path + persistence)
// ---------------------------------------------------------------------------

export function isChangeType(value: unknown): value is ChangeType {
  return (
    typeof value === 'string' &&
    CHANGE_TYPE_OPTIONS.some((o) => o.value === value)
  );
}

export function isComplexityFactor(value: unknown): value is ComplexityFactor {
  return (
    typeof value === 'string' &&
    COMPLEXITY_OPTIONS.some((o) => o.value === value)
  );
}

export function isExtraWorkItem(value: unknown): value is ExtraWorkItem {
  return (
    typeof value === 'string' && EXTRA_WORK_ALL.some((o) => o.value === value)
  );
}

export function isUnknowns(value: unknown): value is Unknowns {
  return (
    typeof value === 'string' && UNKNOWNS_OPTIONS.some((o) => o.value === value)
  );
}

/** Coerces an arbitrary list to a deduped list of valid complexity factors. */
export function coerceComplexity(value: unknown): ComplexityFactor[] {
  if (!Array.isArray(value)) return [];
  const seen = new Set<ComplexityFactor>();
  for (const v of value) if (isComplexityFactor(v)) seen.add(v);
  return [...seen];
}

/** Coerces an arbitrary list to a deduped list of valid extra-work items. */
export function coerceExtraWork(value: unknown): ExtraWorkItem[] {
  if (!Array.isArray(value)) return [];
  const seen = new Set<ExtraWorkItem>();
  for (const v of value) if (isExtraWorkItem(v)) seen.add(v);
  return [...seen];
}

// ---------------------------------------------------------------------------
// Inputs factory + validation + running the engine
// ---------------------------------------------------------------------------

/** A sensible starting point for the wizard: a well-understood feature. */
export function blankEstimateInputs(): EstimateInputs {
  return {
    changeType: 'feature',
    complexity: [],
    extraWork: [],
    unknowns: 'well_understood',
  };
}

/** Builds engine inputs from an AI suggestion (dropping any invalid entries). */
export function inputsFromSuggestion(
  suggestion: AiEstimateSuggestion,
): EstimateInputs {
  // Vector never picks 'custom' (it can't supply a base) — coerce it to the
  // safe 'feature' default if it somehow does.
  const changeType =
    isChangeType(suggestion.changeType) && suggestion.changeType !== 'custom'
      ? suggestion.changeType
      : 'feature';
  return {
    changeType,
    complexity: coerceComplexity(suggestion.complexity),
    extraWork: coerceExtraWork(suggestion.extraWork),
    unknowns: isUnknowns(suggestion.unknowns)
      ? suggestion.unknowns
      : 'well_understood',
  };
}

/** Validates wizard inputs. Returns human-readable problems (empty = valid). */
export function validateEstimateInputs(inputs: EstimateInputs): string[] {
  const problems: string[] = [];
  if (!isChangeType(inputs.changeType)) {
    problems.push('Select the kind of change.');
  }
  if (!isUnknowns(inputs.unknowns)) {
    problems.push('Select how well-understood the work is.');
  }
  if (inputs.changeType === 'custom') {
    if (!inputs.customChangeType?.trim()) {
      problems.push('Name the custom change type.');
    }
    const base = inputs.customBaseDays;
    if (!(typeof base === 'number' && Number.isFinite(base) && base > 0)) {
      problems.push('Enter a base effort (in days) for the custom change type.');
    }
  }
  return problems;
}

/**
 * Runs the deterministic engine on the inputs. Thin re-export so wizard/AI code
 * imports the compute from one place.
 */
export function runEstimate(inputs: EstimateInputs): EstimateResult {
  return estimate(inputs);
}

/**
 * True when the wizard should surface the "consider a Spike" recommendation:
 * major unknowns on a non-spike change (§5.4).
 */
export function shouldRecommendSpike(inputs: EstimateInputs): boolean {
  return inputs.unknowns === 'major' && inputs.changeType !== 'spike';
}

/** Toggles a value in a list (adds if absent, removes if present). */
export function toggleInList<T>(list: T[], value: T): T[] {
  return list.includes(value)
    ? list.filter((v) => v !== value)
    : [...list, value];
}

/** Formats a day count compactly (drops trailing .0; "0.5d", "3d", "1.25d"). */
export function formatDays(days: number): string {
  if (!Number.isFinite(days)) return '—';
  const rounded = Number(days.toFixed(2));
  return `${rounded}d`;
}

// ---------------------------------------------------------------------------
// Manual override of the computed estimate (§ "Override")
// ---------------------------------------------------------------------------

/** The computed headline numbers the override replaces (engine output shape). */
export interface ComputedNumbers {
  effortDays: number;
  storyPoints: number;
  confidence: number;
}

/** The effective (task-facing) numbers + whether they came from an override. */
export interface EffectiveEstimate extends ComputedNumbers {
  overridden: boolean;
}

/**
 * Builds a fresh {@link EstimateOverride} from raw numbers + remark, seeding the
 * `at` timestamp. The remark is trimmed; callers should still run
 * {@link validateOverride} before persisting.
 */
export function makeOverride(args: {
  effortDays: number;
  storyPoints: number;
  confidence: number;
  remark: string;
}): EstimateOverride {
  return {
    effortDays: args.effortDays,
    storyPoints: args.storyPoints,
    confidence: args.confidence,
    remark: args.remark.trim(),
    at: new Date().toISOString(),
  };
}

/**
 * Validates an override. Returns human-readable problems (empty = valid). A
 * remark is REQUIRED; the numbers must be finite and in range.
 */
export function validateOverride(override: EstimateOverride): string[] {
  const problems: string[] = [];
  if (!override.remark.trim()) {
    problems.push('A remark is required to override the estimate.');
  }
  if (!(Number.isFinite(override.effortDays) && override.effortDays >= 0)) {
    problems.push('Enter a valid override effort (days).');
  }
  if (!(Number.isFinite(override.storyPoints) && override.storyPoints >= 0)) {
    problems.push('Enter valid override story points.');
  }
  if (
    !(
      Number.isFinite(override.confidence) &&
      override.confidence >= 0 &&
      override.confidence <= 100
    )
  ) {
    problems.push('Override confidence must be between 0 and 100.');
  }
  return problems;
}

/**
 * Resolves the EFFECTIVE estimate: the override numbers when an override is
 * present, else the computed numbers. `overridden` flags which path was taken.
 * This is the single source of truth for the numbers mirrored onto the task.
 */
export function effectiveEstimate(
  computed: ComputedNumbers,
  override?: EstimateOverride | null,
): EffectiveEstimate {
  if (override) {
    return {
      effortDays: override.effortDays,
      storyPoints: override.storyPoints,
      confidence: override.confidence,
      overridden: true,
    };
  }
  return { ...computed, overridden: false };
}
